(() => {
  const sectionSelector = '[data-verso-ask-carousel]';
  const scrollSnapClass = 'verso-ask-scroll-snap';
  const initializedSections = new WeakSet();
  let snapUpdateFrame = null;
  let releasedAboveSection = null;
  let releasedBelowSection = null;

  const getInteractiveSlideCount = (section) => {
    return section.querySelectorAll('[data-verso-ask-slide]:not([data-verso-ask-decoration])').length;
  };

  const updateScrollSnapState = () => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (releasedAboveSection) {
      const releasedRect = releasedAboveSection.getBoundingClientRect();
      if (releasedRect.top < -1 || releasedRect.top >= viewportHeight) {
        releasedAboveSection = null;
      } else {
        document.documentElement.classList.remove(scrollSnapClass);
        return;
      }
    }

    if (releasedBelowSection) {
      const releasedRect = releasedBelowSection.getBoundingClientRect();
      if (releasedRect.bottom <= 1 || releasedRect.top >= viewportHeight) {
        releasedBelowSection = null;
      } else {
        document.documentElement.classList.remove(scrollSnapClass);
        return;
      }
    }

    const shouldSnap = [...document.querySelectorAll(sectionSelector)].some((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top <= 1 && rect.bottom >= viewportHeight - 1;
    });

    document.documentElement.classList.toggle(scrollSnapClass, shouldSnap);
  };

  const queueScrollSnapUpdate = () => {
    if (snapUpdateFrame !== null) return;
    snapUpdateFrame = window.requestAnimationFrame(() => {
      snapUpdateFrame = null;
      updateScrollSnapState();
    });
  };

  const guardSectionEntryWheel = (event) => {
    if (document.documentElement.classList.contains(scrollSnapClass)) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewportHeight : 1;
    const scrollDelta = event.deltaY * deltaMultiplier;
    if (!scrollDelta) return;

    const crossedSection = [...document.querySelectorAll(sectionSelector)].find((section) => {
      const rect = section.getBoundingClientRect();
      if (scrollDelta > 0) return rect.top > 0 && rect.top <= scrollDelta;

      const sceneHeight = section.querySelector('.verso-ask__scene')?.offsetHeight || viewportHeight;
      const slideCount = getInteractiveSlideCount(section);
      const lastSlideBottom = section.offsetHeight - sceneHeight * Math.max(0, slideCount - 1);
      return rect.bottom < lastSlideBottom && rect.bottom - scrollDelta >= lastSlideBottom;
    });

    if (!crossedSection) return;

    const rect = crossedSection.getBoundingClientRect();
    const sectionTop = window.scrollY + rect.top;
    const sceneHeight = crossedSection.querySelector('.verso-ask__scene')?.offsetHeight || viewportHeight;
    const slideCount = getInteractiveSlideCount(crossedSection);
    const targetTop = scrollDelta > 0
      ? sectionTop
      : sectionTop + sceneHeight * Math.max(0, slideCount - 1);
    event.preventDefault();
    releasedAboveSection = null;
    releasedBelowSection = null;
    document.documentElement.classList.add(scrollSnapClass);
    window.scrollTo({ top: targetTop, behavior: 'auto' });
  };

  const initialize = (section) => {
    if (initializedSections.has(section)) return;
    initializedSections.add(section);

    const scene = section.querySelector('.verso-ask__scene');
    const header = section.querySelector('.verso-ask__header');
    const stage = section.querySelector('.verso-ask__stage');
    const promptPanel = section.querySelector('.verso-ask__prompt-panel');
    const swiperElement = section.querySelector('.verso-ask__swiper');
    const prompts = [...section.querySelectorAll('[data-verso-ask-prompt]')];
    const slides = [...section.querySelectorAll('[data-verso-ask-slide]')];
    const interactiveSlides = slides.filter((slide) => !slide.hasAttribute('data-verso-ask-decoration'));
    const cards = [...section.querySelectorAll('[data-verso-ask-card]')];
    const interactiveCards = cards.filter((card) => !card.closest('[data-verso-ask-decoration]'));
    const slideCount = Math.min(prompts.length, interactiveSlides.length, interactiveCards.length);
    if (!scene || !header || !stage || !promptPanel || !swiperElement || !slideCount) return;

    const updateSlideAccessibility = () => {
      interactiveSlides.slice(0, slideCount).forEach((slide, slideIndex) => {
        slide.setAttribute('aria-label', `${slideIndex + 1} / ${slideCount}`);
      });
    };

    let swiper = null;
    let activeIndex = 0;
    let renderedIndex = -1;
    let updateFrame = null;
    let mobileLayoutFrame = null;
    let lastTouchY = null;

    const updateMobileStageLayout = () => {
      stage.style.removeProperty('--ask-mobile-stage-scale');
      stage.style.removeProperty('--ask-mobile-stage-flow-height');

      if (!window.matchMedia('(max-width: 575px)').matches) return;

      const sceneRect = scene.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      const promptRect = promptPanel.getBoundingClientRect();
      const promptLift = Math.max(0, -Number.parseFloat(window.getComputedStyle(promptPanel).marginTop) || 0);
      const stageStyles = window.getComputedStyle(stage);
      const baseScale = Number.parseFloat(stageStyles.getPropertyValue('--ask-mobile-stage-scale')) || 1;
      const stageRect = stage.getBoundingClientRect();
      const visibleLayers = [...stage.querySelectorAll('.verso-ask__device, .verso-ask__slide')]
        .filter((layer) => Number.parseFloat(window.getComputedStyle(layer).opacity) > 0);
      const visualBottom = Math.max(...visibleLayers.map((layer) => layer.getBoundingClientRect().bottom));
      const naturalVisualHeight = Math.max(1, (visualBottom - stageRect.top) / baseScale);
      const availableHeight = Math.max(1, sceneRect.bottom - headerRect.bottom - promptRect.height + promptLift - 8);
      const nextScale = Math.min(1.15, availableHeight / naturalVisualHeight);
      const nextFlowHeight = naturalVisualHeight * nextScale + 8;

      stage.style.setProperty('--ask-mobile-stage-scale', nextScale.toFixed(4));
      stage.style.setProperty('--ask-mobile-stage-flow-height', `${nextFlowHeight}px`);
    };

    const queueMobileLayoutUpdate = () => {
      if (mobileLayoutFrame !== null) return;
      mobileLayoutFrame = window.requestAnimationFrame(() => {
        mobileLayoutFrame = null;
        updateMobileStageLayout();
      });
    };

    const clampIndex = (index) => Math.min(slideCount - 1, Math.max(0, index));

    const render = (index) => {
      const nextIndex = clampIndex(index);
      activeIndex = nextIndex;
      if (renderedIndex === nextIndex) return;
      renderedIndex = nextIndex;

      if (swiper && !swiper.destroyed && swiper.activeIndex !== activeIndex) {
        swiper.slideTo(activeIndex, 0, false);
      }

      prompts.forEach((prompt, promptIndex) => {
        const isActive = promptIndex === activeIndex;
        prompt.classList.toggle('is-active', isActive);
        prompt.toggleAttribute('aria-current', isActive);
      });

      slides.forEach((slide, slideIndex) => {
        const isDecorative = slide.hasAttribute('data-verso-ask-decoration');
        const interactiveIndex = interactiveSlides.indexOf(slide);
        const decorativeIndex = slideIndex - interactiveSlides.length;
        const slot = isDecorative
          ? slideCount + decorativeIndex
          : (interactiveIndex - activeIndex + slideCount) % slideCount;
        slide.dataset.slot = String(slot);
      });

      interactiveCards.forEach((card, cardIndex) => {
        const slot = (cardIndex - activeIndex + slideCount) % slideCount;
        card.tabIndex = slot === 0 ? -1 : 0;
      });
    };

    const getStepHeight = () => {
      return Math.max(1, scene.offsetHeight);
    };

    const getSectionTop = () => window.scrollY + section.getBoundingClientRect().top;

    const updateFromScroll = () => {
      const progress = -section.getBoundingClientRect().top / getStepHeight();
      render(Math.round(progress));
    };

    const queueScrollUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = null;
        updateFromScroll();
      });
    };

    const scrollToIndex = (index) => {
      const targetIndex = clampIndex(index);
      releasedAboveSection = null;
      releasedBelowSection = null;
      document.documentElement.classList.add(scrollSnapClass);
      render(targetIndex);
      window.scrollTo({
        top: getSectionTop() + getStepHeight() * targetIndex,
        behavior: 'auto',
      });
    };

    prompts.forEach((prompt) => {
      prompt.addEventListener('click', () => scrollToIndex(Number(prompt.dataset.slideIndex)));
    });

    interactiveCards.forEach((card) => {
      card.addEventListener('click', () => {
        const slide = card.closest('[data-verso-ask-slide]');
        if (slide?.dataset.slot === '0') return;
        scrollToIndex(Number(card.dataset.slideIndex));
      });
    });

    const updateBoundaryRelease = (scrollDirection) => {
      const rect = section.getBoundingClientRect();
      const progress = -rect.top / getStepHeight();
      const atFirstSnapPoint = activeIndex === 0 && Math.abs(progress) <= 0.02;
      const atLastSnapPoint = activeIndex === slideCount - 1
        && Math.abs(progress - (slideCount - 1)) <= 0.02;

      if (scrollDirection < 0 && releasedBelowSection === section) {
        releasedBelowSection = null;
        updateScrollSnapState();
        return;
      }

      if (scrollDirection < 0 && atFirstSnapPoint) {
        releasedAboveSection = section;
        document.documentElement.classList.remove(scrollSnapClass);
        return;
      }

      if (scrollDirection > 0 && releasedAboveSection === section) {
        releasedAboveSection = null;
        updateScrollSnapState();
        return;
      }

      if (scrollDirection > 0 && atLastSnapPoint) {
        releasedBelowSection = section;
        document.documentElement.classList.remove(scrollSnapClass);
      }
    };

    section.addEventListener('wheel', (event) => {
      updateBoundaryRelease(event.deltaY);
    }, { passive: true });
    section.addEventListener('touchstart', (event) => {
      lastTouchY = event.touches[0]?.clientY ?? null;
    }, { passive: true });
    section.addEventListener('touchmove', (event) => {
      const currentTouchY = event.touches[0]?.clientY;
      if (lastTouchY === null || currentTouchY === undefined) return;
      updateBoundaryRelease(lastTouchY - currentTouchY);
      lastTouchY = currentTouchY;
    }, { passive: true });
    section.addEventListener('touchend', () => {
      lastTouchY = null;
    }, { passive: true });

    const attachSwiper = () => {
      const instance = swiperElement.flowticaSwiper || swiperElement.swiper;
      if (!instance || instance.destroyed) return false;

      swiper = instance;
      swiper.slideTo(activeIndex, 0, false);
      updateSlideAccessibility();
      return true;
    };

    if (!attachSwiper()) {
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (attachSwiper() || attempts >= 40) window.clearInterval(timer);
      }, 100);
    }

    window.addEventListener('scroll', queueScrollUpdate, { passive: true });
    window.addEventListener('resize', queueScrollUpdate, { passive: true });
    window.addEventListener('resize', queueMobileLayoutUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      const contentObserver = new ResizeObserver(queueMobileLayoutUpdate);
      contentObserver.observe(header);
      contentObserver.observe(promptPanel);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(queueMobileLayoutUpdate);
    }

    render(0);
    window.requestAnimationFrame(() => {
      updateMobileStageLayout();
      updateFromScroll();
      section.classList.add('is-ready');
    });
  };

  const initializeAll = (root = document) => {
    const sections = root.querySelectorAll(sectionSelector);
    sections.forEach(initialize);
    updateScrollSnapState();
  };

  initializeAll();
  window.addEventListener('wheel', guardSectionEntryWheel, { passive: false, capture: true });
  window.addEventListener('scroll', queueScrollSnapUpdate, { passive: true });
  window.addEventListener('resize', queueScrollSnapUpdate, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', queueScrollSnapUpdate);
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest('[data-slide-index]');
    const section = event.target.closest(sectionSelector);
    if (!block || !section) return;

    section
      .querySelector(`[data-verso-ask-prompt][data-slide-index="${block.dataset.slideIndex}"]`)
      ?.click();
  });
})();
